import {NodeView} from "../../../types/Node";
import {useCallback, useContext, useMemo, useState} from "react";
import {ConfirmDialogContext, TextInputWithReset} from "zavadil-react-common";
import {useRestClient} from "../../../client/SpmRestClient";
import {useAlerts} from "../../../utils/UserAlerts";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {Button, Form, Stack} from "react-bootstrap";
import {useUser} from "../../../utils/UserSession";
import {NumberUtil, ObjectUtil} from "zavadil-ts-common";
import NodeForm from "./form/NodeForm";
import NodeTree from "./tree/NodeTree";
import TestFlight from "./test/TestFlight";

export default function NodesPage() {
	const restClient = useRestClient();
	const userAlerts = useAlerts();
	const user = useUser();
	const confirmDialog = useContext(ConfirmDialogContext);

	const [selectedNode, setSelectedNode] = useState<NodeView | null>(null);
	const [selectedNodePath, setSelectedNodePath] = useState<Array<number>>([]);
	const isNodeSelected = useMemo(() => ObjectUtil.notEmpty(selectedNode), [selectedNode]);
	const selectedNodeId = useMemo(() => selectedNode ? selectedNode.id : null, [selectedNode]);

	const [editNode, setEditNode] = useState<NodeView | null>(null);

	const [jumpIdText, setJumpIdText] = useState<string>('');
	const jumpId = useMemo(() => NumberUtil.parseNumber(jumpIdText), [jumpIdText]);

	const jumpToNode = useCallback(
		(nodeId?: number | null) => {
			if (!nodeId) {
				setSelectedNode(null);
				setSelectedNodePath([]);
				return;
			}
			restClient.nodes
				.load(nodeId)
				.then(
					(node) => {
						if (!node) {
							userAlerts.err(`Uzel ${nodeId} nebyl nalezen`);
							return;
						}
						restClient.nodes.nodeTreePath(nodeId)
							.then(
								(path) => {
									setSelectedNode(node);
									setSelectedNodePath(path);
								}
							)
							.catch((e) => userAlerts.err(e));
					}
				)
				.catch((e) => userAlerts.err(e))
		},
		[restClient, userAlerts]
	);

	const deleteNode = useCallback(
		() => {
			if (!selectedNodeId) return;
			confirmDialog.confirm(
				'Smazat otázku?',
				'Opravdu si přejete smazat tuto otázku? Všechny následující otázky budou smazány také!',
				() => {
					restClient.nodes
						.delete(selectedNodeId)
						.then(
							() => {
								userAlerts.info('Otázka byla smazána');
								jumpToNode(selectedNode?.id);
							}
						)
						.catch((e) => userAlerts.err(e));
				}
			);
		},
		[confirmDialog, selectedNodeId, selectedNode, jumpToNode, restClient, userAlerts]
	);

	const addNode = useCallback(
		() => {
			const newNode: NodeView = {
				id: null,
				parentId: selectedNodeId,
				createdBy: user.name,
				changedBy: user.name,
				answer: '',
				answerSecondary: null,
				text: '',
				textSecondary: null,
				googleSearch: null,
				isContact: false,
				isEnabled: true,
				weight: 0,
				userTrackingResult: 0,
				userTrackingScore: 0,
				contactTypeFilters: [],
				links: []
			};
			setEditNode(newNode);
		},
		[user, selectedNodeId]
	);

	return (
		<div>
			<h1>Editace otázek</h1>
			<Row>
				<Col md={8}>
					<Stack className="editor-menu border rounded p-2" gap={2}>
						<Stack direction="horizontal" gap={2}>
							<Button
								variant="success"
								onClick={addNode}
							>Nová</Button>
							<Button
								disabled={!isNodeSelected}
								onClick={() => setEditNode(selectedNode)}
							>Upravit</Button>
							<Button
								disabled={!isNodeSelected}
								variant="danger"
								onClick={deleteNode}
							>Smazat</Button>
						</Stack>
						<Form
							onSubmit={
								(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (jumpId) jumpToNode(jumpId);
								}
							}
						>
							<Stack direction="horizontal" gap={2}>
								<div>Skočit na otázku č.:</div>
								<TextInputWithReset className="search-input" value={jumpIdText} onChange={setJumpIdText}/>
								<Button type="submit" variant="dark" disabled={jumpId === null}>Hledat</Button>
							</Stack>
						</Form>
					</Stack>
					<NodeTree
						node={undefined}
						nodePath={[]}
						selectedNode={selectedNode}
						selectedNodePath={selectedNodePath}
						onNodeSelected={
							(node, path) => {
								setSelectedNode(node);
								setSelectedNodePath(path);
							}
						}
						onNodeDoubleClicked={
							(node, path) => {
								setSelectedNode(node);
								setSelectedNodePath(path);
								setEditNode(node);
							}
						}
					/>
				</Col>
				<Col md={4}>
					<TestFlight
						node={selectedNode}
						onNodeSelected={(n) => jumpToNode(n.id)}
						onSelectParent={() => jumpToNode(selectedNode?.parentId)}
						onJumpToNodeRequested={jumpToNode}
					/>
				</Col>
			</Row>
			{editNode && (
				<NodeForm
					node={editNode}
					onClosed={() => setEditNode(null)}
					onSaved={
						(n) => {
							setEditNode(null);
							jumpToNode(n.id);
						}
					}
				/>
			)}
		</div>
	);
}
